import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import { compose } from 'redux';
import Waypoint from 'react-waypoint';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import { withLogBiEvent, } from '../biLogger/hocs/withLogBiEvent';
import { BiEventType, BiDataHook } from '../biLogger';
import { Url } from '@wix/da-url';
import Flex from '../Flex';
import CommentBox from '../CommentBox';
import LoadingIndicator from '../LoadingIndicator';
import StandardDropdown from '../Dropdown/Preset/StandardDropdown';
import { flatThreadToTree, getCommentBoxId, updateCommentOffset, isActiveThread, } from './helpers';
import { scrollIntoViewWithHeader } from '../utils/scrollIntoViewWithHeader';
import { withMobileContext } from '../Context';
import ThreadComment from './_partials/ThreadComment';
import ThreadPagination from './_partials/ThreadPagination';
import ViewReplies from './_partials/ViewReplies';
import ddt from '@wix/da-ddt';
import { getIndentClassName, getReplyOutdentClassName } from './styles';
import { COMMENT_MODAL_THREAD_MAX_DEPTH } from './constants';
import s from './CommentThreadV2.scss';
var logger = ddt.createLogger('comments');
var CommentThread = /** @class */ (function (_super) {
    __extends(CommentThread, _super);
    function CommentThread() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = { replyingComments: {} };
        _this.threadStartRef = React.createRef();
        _this.lastCommentElement = undefined;
        _this.commentElements = {};
        _this.verticalIndicatorElements = {};
        _this.needsToScroll = false;
        _this.setCommentRef = function (commentId, element) {
            _this.commentElements[commentId] = element;
            var lastInsertedCommentId = _this.props.lastInsertedCommentId;
            var isLastComment = lastInsertedCommentId && lastInsertedCommentId === commentId;
            if (isLastComment) {
                _this.lastCommentElement = element;
            }
        };
        _this.setVerticalIndicatorRef = function (commentId, element) {
            _this.verticalIndicatorElements[commentId] = element;
        };
        _this.onCommentMouseOver = function (commentId, parentId) {
            if (!parentId ||
                !_this.commentElements[commentId] ||
                !_this.verticalIndicatorElements[commentId] ||
                _this.state.replyingComments[parentId]) {
                return;
            }
            var _a = _this.props, parentComment = _a.parentComment, parentCommentRef = _a.parentCommentRef;
            var parentNode = null;
            if (parentComment &&
                parentComment.commentId === parentId &&
                parentCommentRef) {
                parentNode = parentCommentRef.current;
            }
            else {
                parentNode = _this.commentElements[parentId];
            }
            if (!parentNode) {
                return;
            }
            var commentTop = _this.commentElements[commentId].getBoundingClientRect().top;
            var parentTop = parentNode.getBoundingClientRect().top;
            _this.verticalIndicatorElements[commentId].style.height = commentTop -
                parentTop -
                20 + "px";
        };
        _this.onEditingStateChange = function (commentId, state) {
            var _a;
            var _b = _this.state.replyingComments, _c = commentId, value = _b[_c], rest = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
            if (state === 'replying') {
                _this.setState({ replyingComments: __assign(__assign({}, rest), (_a = {}, _a[commentId] = true, _a)) });
            }
            else {
                _this.setState({ replyingComments: rest });
            }
        };
        _this.onClickLoadMore = function () {
            var _a = _this.props, fetchMore = _a.fetchMore, streamId = _a.streamId, logBiEvent = _a.logBiEvent, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId;
            logBiEvent({
                evid: BiEventType.LOAD_MORE_COMMENTS_CLICK,
                itemid: commentableItemId,
                typeid: commentableTypeId,
            });
            return fetchMore(streamId);
        };
        _this.onClickLoadPrevious = function () {
            var _a = _this.props, fetchPrevious = _a.fetchPrevious, streamId = _a.streamId;
            fetchPrevious(streamId);
        };
        _this.onOrderChanged = function (value) {
            var _a = _this.props, onOrderChanged = _a.onOrderChanged, streamId = _a.streamId;
            onOrderChanged && onOrderChanged(streamId, value);
        };
        _this.onThreadUpdated = function (result) {
            var _a = _this.props, onThreadUpdated = _a.onThreadUpdated, streamId = _a.streamId;
            onThreadUpdated && onThreadUpdated(streamId, result);
        };
        _this.openReplies = function (commentId, comment) {
            var _a = _this.props, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, onOpenCommentModal = _a.onOpenCommentModal, logBiEvent = _a.logBiEvent;
            logBiEvent({
                evid: BiEventType.CLICK_VIEW_REPLY_TO_COMMENT,
                itemid: commentableItemId,
                typeid: commentableTypeId,
                commentid: commentId,
                commenterid: comment.user.useridUuid,
            });
            // If we already in iframe just redirect
            if (window.self !== window.top) {
                window.location.assign(Url.commentLink(commentableTypeId, commentableItemId, commentId, undefined, true));
                return;
            }
            onOpenCommentModal(commentableTypeId, commentableItemId, commentId, comment);
        };
        _this.scrollToLatestComment = function () {
            if (!_this.lastCommentElement) {
                _this.needsToScroll = true;
                return;
            }
            logger.log('Scrolling to latest comment');
            scrollIntoViewWithHeader(_this.lastCommentElement, _this.props.isMobile);
        };
        _this.renderRecursiveComment = function (comment, index) {
            var _a = _this.props, currentUser = _a.currentUser, streamId = _a.streamId, enableOffsetTracking = _a.enableOffsetTracking, t = _a.t, canPostComment = _a.canPostComment, compact = _a.compact, isModal = _a.isModal, maxDepth = _a.maxDepth, isMobile = _a.isMobile;
            var commentId = comment.commentId, indent = comment.indent, isLeafComment = comment.isLeafComment, children = comment.children, parentId = comment.parentId, replies = comment.replies;
            var hasMoreReplies = isLeafComment && replies > 0 && indent >= maxDepth;
            var replyClassName = _this.getReplyClassName(indent);
            var indentClassName = getIndentClassName(indent);
            return (React.createElement(React.Fragment, { key: "comment-wrap-" + index },
                React.createElement("div", { className: classnames(indentClassName, s['comment-wrapper'], parentId &&
                        !_this.state.replyingComments[parentId] &&
                        s['comment-wrapper-child'], hasMoreReplies && s['has-more-replies']), ref: _this.setCommentRef.bind(_this, commentId), "data-hook": BiDataHook.COMMENTS_THREAD_ITEM, onMouseOver: _this.onCommentMouseOver.bind(_this, commentId, parentId) },
                    parentId && (React.createElement("div", { className: s['vertical-indicator'], ref: _this.setVerticalIndicatorRef.bind(_this, commentId) })),
                    React.createElement(ThreadComment, { key: "comment-" + commentId, className: s['comment'], replyClassName: replyClassName, actionItemClassName: classnames(isModal && s['modal-comment-action-item']), streamId: streamId, indent: indent, currentUser: currentUser, comment: comment, enableOffsetTracking: enableOffsetTracking, canPostComment: !!canPostComment, compact: compact, onEditingStateChange: _this.onEditingStateChange.bind(_this, commentId) })),
                children && children.map(_this.renderRecursiveComment),
                hasMoreReplies && (React.createElement(Flex, { className: classnames(s['view-replies-wrapper'], isMobile && indentClassName), justifyContent: "flex-end" },
                    React.createElement(ViewReplies, { onClick: _this.openReplies.bind(_this, commentId, comment) }, t('commentthread.control.viewReplies'))))));
        };
        return _this;
    }
    CommentThread.prototype.componentDidMount = function () {
        var _a = this.props, isInitialized = _a.isInitialized, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, isMobile = _a.isMobile, parentComment = _a.parentComment, parentCommentRef = _a.parentCommentRef;
        if (!isInitialized) {
            this.onStreamIdChanged();
        }
        if (isActiveThread(commentableTypeId, commentableItemId) &&
            this.threadStartRef.current) {
            // TODO: this will need perspective context if used in profiles
            logger.log('Scrolling to active thread');
            scrollIntoViewWithHeader(this.threadStartRef.current, isMobile);
        }
        if (parentComment && parentCommentRef) {
            this.setCommentRef(parentComment.commentId, parentCommentRef.current);
        }
    };
    CommentThread.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, lastInsertedCommentId = _a.lastInsertedCommentId, streamId = _a.streamId, isInitialized = _a.isInitialized;
        if (prevProps.streamId !== streamId && !isInitialized) {
            this.onStreamIdChanged();
        }
        // there is some weird hiccup in the react lifecycle atm
        // where the refs dont correctly get set on the first update,
        // there is an immediate second render that sets the ref
        // so we do a little shenanigan here to try the scrolling again
        if ((lastInsertedCommentId &&
            prevProps.lastInsertedCommentId !== lastInsertedCommentId) ||
            this.needsToScroll) {
            this.needsToScroll = false;
            this.scrollToLatestComment();
        }
    };
    CommentThread.prototype.onStreamIdChanged = function () {
        var _a = this.props, commentableItemId = _a.commentableItemId, commentableTypeId = _a.commentableTypeId, maxDepth = _a.maxDepth, parentId = _a.parentId, order = _a.order, streamId = _a.streamId, onStreamIdChanged = _a.onStreamIdChanged, _b = _a.allowSelfInit, allowSelfInit = _b === void 0 ? true : _b, isModal = _a.isModal;
        if (!allowSelfInit) {
            return;
        }
        onStreamIdChanged({
            commentableItemId: commentableItemId,
            commentableTypeId: commentableTypeId,
            parentId: parentId,
            order: order,
            streamId: streamId,
            maxDepth: isModal ? COMMENT_MODAL_THREAD_MAX_DEPTH : maxDepth,
        });
    };
    CommentThread.prototype.getReplyClassName = function (indent) {
        var _a = this.props, isModal = _a.isModal, useCardLayout = _a.useCardLayout, isMobile = _a.isMobile;
        if (useCardLayout) {
            // Card layout is used by profile widget where we have to
            // outdent the form all the way to the left
            return getReplyOutdentClassName(indent);
        }
        if (isMobile) {
            return undefined;
        }
        if (isModal) {
            // Inside a comment modal reply forms must be outdented all the way
            // to the left
            return getReplyOutdentClassName(indent + 1);
        }
        if (indent >= 5) {
            // Reply forms should be at least as wide as level 5
            return getReplyOutdentClassName(indent - 4);
        }
        return undefined;
    };
    CommentThread.prototype.render = function () {
        return (React.createElement(Waypoint, { onLeave: function () { return updateCommentOffset(); } }, this.renderThread()));
    };
    CommentThread.prototype.renderThread = function () {
        var _a = this.props, className = _a.className, items = _a.items, streamId = _a.streamId, _b = _a.hasMore, hasMore = _b === void 0 ? false : _b, _c = _a.hasLess, hasLess = _c === void 0 ? false : _c, isInfinite = _a.isInfinite, _d = _a.isFetching, isFetching = _d === void 0 ? false : _d, t = _a.t, postbox = _a.postbox, useCardLayout = _a.useCardLayout, isModal = _a.isModal;
        var isTopPostboxVisible = postbox === 'top' || postbox === 'both';
        var isBottomPostboxVisible = postbox === 'bottom' ||
            (postbox === 'both' && items && items.length >= 6);
        return (React.createElement("div", { ref: this.threadStartRef, className: classnames(s['root'], useCardLayout && s['card-layout'], isModal && s['modal'], className), "data-hook": BiDataHook.COMMENTS_THREAD },
            this.renderStatsAndControls(),
            React.createElement("div", { className: s['scrollable'] },
                React.createElement("div", { className: s['main-area'] },
                    isTopPostboxVisible && this.renderPostbox('top'),
                    React.createElement(ThreadPagination, { hasMore: hasLess, isFetching: isFetching, streamId: streamId, onLoadMore: this.onClickLoadPrevious }, t('commentthread.control.loadprev')),
                    this.renderThreadStack(),
                    this.renderLoader(),
                    React.createElement(ThreadPagination, { className: s['load-more'], hasMore: hasMore, isInfinite: isInfinite, isFetching: isFetching, streamId: streamId, onLoadMore: this.onClickLoadMore }, t('commentthread.control.loadmore')))),
            isBottomPostboxVisible && this.renderPostbox('bottom')));
    };
    CommentThread.prototype.renderThreadStack = function () {
        var _a = this.props, items = _a.items, maxDepth = _a.maxDepth;
        if (!items || items.length === 0) {
            return null;
        }
        var renderItems = flatThreadToTree(items, maxDepth);
        return (renderItems && (React.createElement("div", { className: s['thread-stack'] }, renderItems.map(this.renderRecursiveComment))));
    };
    CommentThread.prototype.renderPostbox = function (position) {
        var _a = this.props, currentUser = _a.currentUser, commentableTypeId = _a.commentableTypeId, commentableItemId = _a.commentableItemId, parentComment = _a.parentComment, canPostComment = _a.canPostComment, compact = _a.compact;
        return (React.createElement("div", { className: classnames(s['commentbox-wrapper'], position === 'bottom' && s['commentbox-wrapper-bottom']) },
            React.createElement(CommentBox, { id: getCommentBoxId(commentableTypeId, commentableItemId, position), className: s['commentbox'], commentableType: commentableTypeId, commentableItemId: commentableItemId, replyComment: parentComment, user: currentUser, onPublishSuccess: this.onThreadUpdated, tabIndexBase: position === 'top' ? 0 : 10, canPostComment: !!canPostComment, compact: compact })));
    };
    CommentThread.prototype.renderLoader = function () {
        var isFetching = this.props.isFetching;
        return isFetching && React.createElement(LoadingIndicator, { view: "llama" });
    };
    CommentThread.prototype.renderStatsAndControls = function () {
        var _a = this.props, t = _a.t, total = _a.total, _b = _a.statsAndControls, statsAndControls = _b === void 0 ? 'on' : _b;
        if (statsAndControls === 'off') {
            return;
        }
        return (React.createElement(Flex, { className: s['header'], alignItems: "center", dataHook: BiDataHook.COMMENTS_THREAD_STATS },
            React.createElement("div", { "data-hook": BiDataHook.COMMENTS_THREAD_COUNT }, ['on', 'title'].includes(statsAndControls) && (React.createElement(React.Fragment, null,
                React.createElement("span", { className: s['title'] }, t('commentthread.control.comments')),
                total && React.createElement("span", { className: s['total'] }, total)))),
            ['on', 'order'].includes(statsAndControls) && (React.createElement("div", { className: s['order-select'], "data-hook": BiDataHook.COMMENTS_THREAD_SORTING }, this.renderOrder()))));
    };
    CommentThread.prototype.renderOrder = function () {
        var _a = this.props, t = _a.t, order = _a.order, isLoggedIn = _a.isLoggedIn, isMobile = _a.isMobile;
        if (!isLoggedIn) {
            return null;
        }
        var items = [
            {
                label: t('commentthread.control.newest'),
                value: 'newest',
            },
            {
                label: t('commentthread.control.oldest'),
                value: 'oldest',
            },
        ];
        return (React.createElement(StandardDropdown, { menuAlignment: isMobile ? 'right' : 'left', toggleClassName: s['order-select-toggle'], arrowClassName: s['order-select-arrow'], selectedValue: order, items: items, onSelect: this.onOrderChanged }));
    };
    return CommentThread;
}(React.Component));
export { CommentThread };
var enhance = compose(withLogBiEvent, withMobileContext, withTranslation());
export default enhance(CommentThread);
//# sourceMappingURL=CommentThreadV2.js.map